import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChevronRight, Layers } from 'lucide-react';
import type { ChangeOrder, ProposalBuilderData } from '../../types';
import {
  computePhaseCommitAmount,
  getPhaseStatusLabel,
  groupNodeSetsByPhase,
  isPhasedProposal,
} from '../../utils/proposalPhases';
import { subscribeChangeOrdersForDeal } from '../../utils/changeOrders';
import './PortalPhaseSummaryCard.css';

interface PortalPhaseSummaryCardProps {
  proposal: ProposalBuilderData;
  /** Navigate to the proposal page anchored at the given phase. */
  onReviewPhase: (phaseAnchorId: string) => void;
}

const fmt = (n: number) => `$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

/**
 * Client portal timeline card for phased projects: committed phases with
 * status, future phases greyed, offered phases with a "Review & commit" CTA
 * linking into the proposal page.
 */
const PortalPhaseSummaryCard: React.FC<PortalPhaseSummaryCardProps> = ({
  proposal,
  onReviewPhase,
}) => {
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);

  useEffect(() => {
    if (!proposal.dealId || !isPhasedProposal(proposal)) return;
    return subscribeChangeOrdersForDeal(proposal.dealId, setChangeOrders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.dealId, proposal.phasingEnabled]);

  if (!isPhasedProposal(proposal)) return null;
  const groups = groupNodeSetsByPhase(proposal);
  if (groups.length === 0) return null;

  const proposalSigned = proposal.status === 'approved' || !!proposal.signature;

  return (
    <div className="cps-phase-card" role="region" aria-label="Project phases">
      <div className="cps-phase-card-header">
        <Layers size={15} aria-hidden />
        <span>Project phases</span>
      </div>
      <div className="cps-phase-list">
        {groups.map(({ phase, nodeSets }, idx) => {
          const co = phase.changeOrderId
            ? changeOrders.find(c => c.id === phase.changeOrderId)
            : undefined;
          const amount = co ? co.deltaAmount : computePhaseCommitAmount(proposal, phase.id);
          const readyToCommit = phase.status === 'offered' && proposalSigned && !!co;
          return (
            <div key={phase.id} className={`cps-phase-row status-${phase.status}`}>
              <div className="cps-phase-row-top">
                <span className="cps-phase-title">
                  Phase {idx + 1} — {phase.name}
                </span>
                <span className={`cps-phase-status status-${phase.status}`}>
                  {phase.status === 'committed' && <CheckCircle2 size={11} aria-hidden />}
                  {phase.status === 'offered' && !proposalSigned
                    ? 'In your proposal'
                    : getPhaseStatusLabel(phase.status)}
                </span>
              </div>
              <div className="cps-phase-row-bottom">
                <span className="cps-phase-structures">
                  {nodeSets.length > 0 ? nodeSets.map(ns => ns.name).join(', ') : 'Scope to be defined'}
                </span>
                <span className="cps-phase-amount">
                  {fmt(amount)}
                  {phase.status === 'future' && <span className="cps-phase-estimate"> est.</span>}
                </span>
              </div>
              {readyToCommit && (
                <button
                  type="button"
                  className="cps-action-btn cps-btn-primary cps-phase-commit-btn"
                  onClick={() => onReviewPhase(`phase-${phase.id}`)}
                >
                  Review &amp; commit
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PortalPhaseSummaryCard;
